<script setup>

import { useWebsiteStore } from "@/stores/website";
import { useActiveDeviceRenderStore } from "@/stores/activeDeviceRender";
import { computed, nextTick, onBeforeUnmount, onMounted, onUnmounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { gql } from "graphql-tag";
import { useQuery } from "@vue/apollo-composable";
import { storeToRefs } from "pinia";
import { EventBus } from "@/events/eventBus";
import Spinner from "@/components/Spinner.vue";
import { useUserStore } from "@/stores/user";
import LlmWatchedAnswer from "@/components/LlmWatchedAnswer.vue";

const userStore = useUserStore();
const {user} = storeToRefs(userStore);
const websiteStore = useWebsiteStore();
const {website, ignoredIssues} = storeToRefs(websiteStore);
const activeDeviceRender = useActiveDeviceRenderStore();
const route = useRoute();
const router = useRouter();
const props = defineProps({
  devicePreview: Object,
  feedbackCategory: {
    type: String,
    default: "proofread",
  },
});
const qaItems = ref([]);
const showTrackingForm = ref(false)
const loaded = ref(false);

const imageRoot = import.meta.env.VITE_IMAGE_ROOT
onMounted(() => {
  EventBus.on('onIconAnnotationClick', onIconAnnotationClick);
  EventBus.on('onIconAnnotationHover', onIconAnnotationHover);
  EventBus.on('onIconAnnotationLeave', onIconAnnotationLeave);
})

onUnmounted(() => {
  EventBus.off('onIconAnnotationClick', onIconAnnotationClick);
  EventBus.off('onIconAnnotationHover', onIconAnnotationHover);
  EventBus.off('onIconAnnotationLeave', onIconAnnotationLeave);
});

function onIconAnnotationClick(annotation) {
  let item = qaItems.value.find(q => q.id === annotation.id);
  if (item) {
    if (route.query.q === item.id) {
      const newQuery = {...route.query};
      delete newQuery.q;
      router.push({query: newQuery});

      // First ensure it's expanded
      const id = `qa_content_${item.id}`;
      const el = document.getElementById(id);
      if (!el) return;
      const collapse = bootstrap.Collapse.getOrCreateInstance(el, {
        toggle: false
      });
      // if (item.expanded) {
      collapse.hide();
      // }
    } else {
      focusLLM(item, 0);
    }

  }


}

function onIconAnnotationHover(annotation) {
  let item = qaItems.value.find(q => q.id === annotation.id);
  if (item) {
    item.hover = true;
  }

}

function onIconAnnotationLeave(annotation) {
  let item = qaItems.value.find(q => q.id === annotation.id);
  if (item) {
    item.hover = false;
  }
}

const SELECT_CHAT = gql`
  query Proofreads($pageVersionId: bigint!,  $lastUpdated: timestamptz) {
    ai_proofread(where: {page_version_id: {_eq: $pageVersionId}, pageversion: { website: {last_updated: {_eq: $lastUpdated}}} }, order_by: {created_at: desc}, limit: 1) {
      id
      created_at
      status
      processed_suggested_questions
      pageversion {
        id
        canonical_page {
          id
          tracked_questions(where: {status: {_eq: "active"}}, order_by: {id: desc}){
            id
            question
            status
            tracked_answers(order_by: { id: desc }, limit: 5){
              id
              completed
              page_version_id
              status
              result
              openai_status
              openai_result
              gemini_status
              gemini_result
            }
          }
        }
      }
    }
  }`
const selectChatVariables = computed(() => ({
  pageVersionId: activeDeviceRender.page.latestVersionId,
  lastUpdated: website.value.lastUpdated,
}));
const {result, refetch} = useQuery(SELECT_CHAT, selectChatVariables, () => ({
      enabled: selectChatVariables.value.deviceRenderId !== 0,
    })
)
watch(
    result,
    data => {
      if (!(data && data.ai_proofread[0])) {
        return
      }
      let ratio = props.devicePreview.ratio();
      let annotations = [];
      loaded.value = true;


      let trackedQuestions = data.ai_proofread[0].pageversion.canonical_page.tracked_questions || [];
      qaItems.value = [];
      for (let question of trackedQuestions) {

        let history = {
          site: [],
          openai: [],
          gemini: [],
        }

        const reversedAnswers = [...question.tracked_answers].reverse();
        for (let answer of reversedAnswers) {
          if (!answer.completed) {
            history.site.push({status: 'Pending', color: 'gray', id: answer.id})
            history.openai.push({status: 'Pending', color: 'gray', id: answer.id})
            history.gemini.push({status: 'Pending', color: 'gray', id: answer.id})

          } else {
            if (answer.result?.answered) {
              if (answer.result?.answer_updated) {
                history.site.push({status: 'Answer Updated', color: 'orange', id: answer.id})
              } else {
                history.site.push({status: 'Stable', color: 'green', id: answer.id})
              }
            } else {
              history.site.push({status: 'Not Answered', color: 'red', id: answer.id})
            }
            if (answer.openai_result?.answered) {
              if (answer.openai_result?.verification?.correct) {
                history.openai.push({status: 'Correct', color: 'green', id: answer.id})
              } else {
                history.openai.push({status: 'Mismatch', color: 'orange', id: answer.id})
              }
            } else {
              history.openai.push({status: 'Not Answered', color: 'red', id: answer.id})
            }

            if (answer.gemini_status === 'skipped') {
            } else if (answer.gemini_result?.answered) {
              if (answer.gemini_result?.verification?.correct) {
                history.gemini.push({status: 'Correct', color: 'green', id: answer.id})
              } else {
                history.gemini.push({status: 'Mismatch', color: 'orange', id: answer.id})
              }
            } else {
              history.gemini.push({status: 'Not Answered', color: 'red', id: answer.id})
            }


          }
        }

        let missing = 5 - history.site.length;
        for (let i = 0; i < missing; i++) {
          history.site.push({status: 'Scheduled Check', color: 'gray', id: 0})
          history.openai.push({status: 'Scheduled Check', color: 'gray', id: 0})
          history.gemini.push({status: 'Scheduled Check', color: 'gray', id: 0})
        }

        let trackedAnswer = question.tracked_answers.find((a) => a.page_version_id === activeDeviceRender.page.latestVersionId) || {};
        let siteLogo = website.value.previewFavicon ? `${imageRoot}${website.value.previewFavicon}?tr=w-24` : "/assets/media/favicons/favicon.png"
        let qaLoading = false;
        if (['pending', 'in_progress'].includes(trackedAnswer.openai_status) || ['pending', 'in_progress'].includes(trackedAnswer.gemini_status)) {
          qaLoading = true;
        }
        if (trackedAnswer.openai_result?.answered && !trackedAnswer.openai_result?.verification) {
          qaLoading = true;
        }

        const valid = {
          site: trackedAnswer.result?.answered,
          openai: trackedAnswer.openai_result?.answered && trackedAnswer.openai_result?.verification?.correct,
          gemini: trackedAnswer.gemini_status === 'skipped' || (trackedAnswer.gemini_result?.answered && trackedAnswer.gemini_result?.verification?.correct)
        }

        qaItems.value.push({
          id: `${question.id}`,
          question: question.question,
          loading: qaLoading,
          expanded: false,
          approved: true,
          hasIssues: !(valid.site && valid.openai && valid.gemini),
          answers: {
            site: {
              source: "site",
              model: "Reference Answer",
              subtitle: "From your page",
              box: trackedAnswer.result?.question_box,
              logo: siteLogo,
              status: trackedAnswer.status,
              answered: trackedAnswer.result?.answered,
              canonicalChecked: !!trackedAnswer.result?.answered,
              matchesCanonical: trackedAnswer.result?.answered,
              matchesCanonicalReason: '',
              showFull: false,
              response: trackedAnswer.result?.answer || '',
              answerReason: trackedAnswer.result?.answer_reason,
              // Last 5 checks: true = stable/found, false = changed/not found
              history: history.site
            },
            openai: {
              source: "openai",
              model: "ChatGPT",
              subtitle: "From asking ChatGPT directly",
              logo: "/assets/images/ChatGPT_logo.svg",
              status: trackedAnswer.openai_status,
              canonicalChecked: !!trackedAnswer.openai_result?.verification,
              matchesCanonical: trackedAnswer.openai_result?.verification?.correct,
              matchesCanonicalReason: trackedAnswer.openai_result?.verification?.error_reason,
              showFull: false,
              answered: trackedAnswer.openai_result?.answered,
              answerReason: trackedAnswer.openai_result?.answer_reason,
              response: trackedAnswer.openai_result?.answer || '',
              history: history.openai
            },
            gemini: {
              source: "gemini",
              model: "Gemini",
              subtitle: "From asking Gemini directly",
              logo: "/assets/images/gemini-color.svg",
              status: trackedAnswer.gemini_status,
              canonicalChecked: !!trackedAnswer.gemini_result?.verification,
              matchesCanonical: trackedAnswer.gemini_result?.verification?.correct,
              matchesCanonicalReason: trackedAnswer.gemini_result?.verification?.error_reason,
              showFull: false,
              response: trackedAnswer.gemini_result?.answer || '',
              answered: trackedAnswer.gemini_result?.answered,
              answerReason: trackedAnswer.gemini_result?.answer_reason,
              history: history.gemini
            },

          }
        })

        let box = trackedAnswer.result?.question_box || {}
        let loading = ['pending', 'in_progress'].includes(trackedAnswer.status) || false;
        annotations.push({
          component: 'icon-annotation',
          x: (box.x * ratio),
          y: (box.y * ratio),
          id: `${question.id}`,
          loading: loading,
          label: '',
        })
      }

      showTrackingForm.value = qaItems.value.length > 0;

      let processedSuggestedQuestions = data.ai_proofread[0].processed_suggested_questions.questions || [];
      for (let suggestedQuestion of processedSuggestedQuestions) {
        // avoid overlapping suggestions
        if (annotations.find((a) => (Math.abs(a.x - suggestedQuestion.box.x * ratio) < 20 && Math.abs(a.y - suggestedQuestion.box.y * ratio) < 20))) {
          continue
        }

        annotations.push({
          id: `a-${suggestedQuestion.box.x}-${suggestedQuestion.box.y}`,
          component: 'expanding-pill',
          x: (suggestedQuestion.box.x * ratio),
          y: (suggestedQuestion.box.y * ratio),
          question: suggestedQuestion.question,
        })
      }
      props.devicePreview.setAnnotations(annotations);
      nextTick(() => {
        initPopovers();
      })

      if (route.query.q) {
        nextTick(() => {
          let item = qaItems.value.find(q => q.id == route.query.q);
          if (item) {
            const id = `qa_content_${item.id}`;
            const el = document.getElementById(id);
            if (el) {
              const collapse = bootstrap.Collapse.getOrCreateInstance(el, {
                toggle: false
              });
              collapse.show();
            }
          }
        });
      }

    },
    {
      immediate: true,
    }
)


watch(() => route.query.q, (newQ) => {
  if (newQ) {
    let item = qaItems.value.find(q => q.id == newQ);
    if (item && !item.expanded) {
      props.devicePreview.scrollToBox({top: item.answers.site.box.y, height: item.answers.site.box.height})
      nextTick(() => {
        const id = `qa_content_${item.id}`;
        const el = document.getElementById(id);
        if (el) {
          const collapse = bootstrap.Collapse.getOrCreateInstance(el, {
            toggle: false
          });
          collapse.show();
        }
      });
    }
  } else {
    // If q is cleared, collapse all
    qaItems.value.forEach(item => {
      if (item.expanded) {
        const id = `qa_content_${item.id}`;
        const el = document.getElementById(id);
        if (el) {
          const collapse = bootstrap.Collapse.getOrCreateInstance(el, {
            toggle: false
          });
          collapse.hide();
        }
      }
    });
  }
}, {immediate: false});


const qaAccordionId = `qa-accordion-${Math.random().toString(36).slice(2)}`
const newQuestion = ref("");

function toggleExpand(item) {
  const id = `qa_content_${item.id}`;

  if (route.query.q != item.id) {
    router.push({
      query: {
        ...route.query,
        q: item.id
      }
    });
  } else {
    const newQuery = {...route.query};
    delete newQuery.q;
    router.push({query: newQuery});
  }

  const el = document.getElementById(id);
  if (!el) return;

  const collapse = bootstrap.Collapse.getOrCreateInstance(el, {
    toggle: false
  });

  collapse.toggle();

}

// Helper variables
let popoverTriggerList = [];
let popoverList = [];

function initPopovers() {
  popoverList.forEach((popover) => popover.dispose());
  popoverTriggerList = [].slice.call(
      document.querySelectorAll('[data-bs-toggle="popover"]')
  );
  popoverList = popoverTriggerList.map(function (popoverTriggerEl) {
    return new window.bootstrap.Popover(popoverTriggerEl, {
      container: popoverTriggerEl.dataset.bsContainer || "#page-container",
      animation:
          popoverTriggerEl.dataset.bsAnimation &&
          popoverTriggerEl.dataset.bsAnimation.toLowerCase() == "true"
              ? true
              : false,
      trigger: popoverTriggerEl.dataset.bsTrigger || "hover focus",
    });
  });
}

onMounted(() => {

  initPopovers();

  qaItems.value.forEach((item) => {
    const id = `qa_content_${item.id}`;
    const el = document.getElementById(id);
    if (el) {
      el.addEventListener('shown.bs.collapse', () => {
        item.expanded = true;
      });
      el.addEventListener('hidden.bs.collapse', () => {
        item.expanded = false;
      });
    }
  });
});

onBeforeUnmount(() => {
  popoverList.forEach((popover) => popover.dispose());

  qaItems.value.forEach((item) => {
    const id = `qa_content_${item.id}`;
    const el = document.getElementById(id);
    if (el) {
      // Remove listeners if needed
    }
  });
});


function focusLLM(item, ansIndex) {

  // Set query parameter
  if (route.query.q != item.id) {
    router.push({
      query: {
        ...route.query,
        q: `${item.id}`
      }
    });
  }

  // First ensure it's expanded
  const id = `qa_content_${item.id}`;
  const el = document.getElementById(id);
  if (!el) return;

  const collapse = bootstrap.Collapse.getOrCreateInstance(el, {
    toggle: false
  });
  if (!item.expanded) {
    collapse.show();
  }
}


async function addQuestion() {
  let q = newQuestion.value;
  newQuestion.value = '';
  await websiteStore.addQuestion(q, 'active', activeDeviceRender.page.externalId)
}

async function deletePrompt(prompt) {
  await websiteStore.deleteQuestion(prompt.id, activeDeviceRender.page.externalId)
}


</script>

<template>
  <div v-if="!loaded" class="ms-2 p-4 text-center">
    <p class="text-muted">Loading Prompts</p>
    <spinner style="width:24px;height:24px"></spinner>
  </div>
  <div v-else class="mt-2 p-0">

    <div v-if="qaItems.length === 0" class="px-5 py-4 text-center border-bottom bg-white rounded-bottom">
      <div class="mb-3">
        <div class="d-inline-flex align-items-center justify-content-center bg-light rounded-circle"
             style="width: 48px; height: 48px;">
          <i class="fa fa-wand-magic-sparkles text-primary opacity-75" style="font-size: 1.25rem;"></i>
        </div>
      </div>
      <h5 class="h6 fw-bold mb-1">No prompts are being watched for this page yet.</h5>
      <p class="text-muted mx-auto mb-3" style="font-size: 0.75rem; max-width: 320px;">
        Track how ChatGPT and Gemini answer questions about your page, and how the answers evolve.
      </p>
      <button v-show="!showTrackingForm"
              class="btn btn-sm btn-primary rounded-pill px-4 shadow-sm"
              @click="showTrackingForm = true"
      >
        <i class="fa fa-plus me-1"></i> Watch your first prompt
      </button>
    </div>

    <div v-show="showTrackingForm" class="add-question-container p-3 pt-2 bg-light border-top mt-auto">
      <div class="d-flex align-items-center gap-2">
        <div class="input-group input-group-sm shadow-sm">
            <span class="input-group-text bg-white border-end-0 text-muted px-2">
              <i class="fa fa-plus small"></i>
            </span>
          <input
              v-model="newQuestion"
              type="text"
              class="form-control border-start-0 ps-0"
              placeholder="Ask a question about this page..."
              @keyup.enter="addQuestion({q: newQuestion})"
          >
          <button class="btn btn-primary px-3" @click="addQuestion({q: newQuestion})">
            Watch
          </button>
        </div>
      </div>

    </div>

    <div class="mt-2">
      <div class="qa-list-container list-group list-group-flush mb-1" :id="qaAccordionId">
        <div v-for="(item, index) in qaItems" :key="item.id" class="list-group-item p-0 border-0">
          <div :class="{'issue-header-hover': item.hover}">
            <div
                class="issue-header collapsed ps-3 py-3"
                data-bs-toggle="collapse"
                :data-bs-target="`#qa_content_${item.id}`"
                aria-expanded="false"
                @click="toggleExpand(item)"
                role="button"
                style="border-bottom: 1px solid #f1f3f5;"
            >
              <div class="d-flex align-items-center flex-grow-1 issue-text text-truncate">
                <div class="status-indicator flex-shrink-0 d-flex align-items-center justify-content-center me-2"
                     style="width: 22px;">
                  <span v-if="item.loading" class="fa fa-spinner fa-spin text-primary"
                        style="font-size: 0.75rem;"></span>
                  <div
                      v-else-if="!item.hasIssues"
                      class="status-icon-box bg-soft-success text-success"
                      title="All answers stable and matching"
                  >
                    <i class="fa fa-check"></i>
                  </div>
                  <div
                      v-else
                      class="status-icon-box bg-soft-warning text-warning-emphasis"
                      title="Issues detected"
                  >
                    <i class="fa fa-exclamation"></i>
                  </div>
                </div>
                <span class="text-truncate text-dark" :title="item.question">{{ item.question }}</span>
              </div>
              <i class="fa-solid fa-chevron-right chevron ms-2"></i>
            </div>
          </div>

          <div :id="`qa_content_${item.id}`" class="collapse"
               :data-bs-parent="`#${qaAccordionId}`">
            <div class="details ps-4 pe-3 py-3 bg-light-subtle">
              <div class="mb-3 d-flex align-items-center justify-content-between">
                <div class="d-flex align-items-center gap-2">
                  <div class="mini-icon-box bg-primary-light" style="width: 20px; height: 20px; font-size: 0.65rem;">
                    <i class="fa fa-magnifying-glass-chart text-primary"></i>
                  </div>
                  <div class="fw-bold text-muted uppercase tracking-wider" style="font-size: 0.6rem;">
                    Answer Comparison
                  </div>
                </div>
                <div class="d-flex gap-2">
                  <button
                      class="btn btn-sm btn-alt-danger d-flex align-items-center justify-content-center p-0 rounded-circle"
                      style="width: 24px; height: 24px; font-size: 0.7rem;"
                      title="Delete Prompt"
                      @click="deletePrompt(item)"
                  >
                    <i class="fa fa-trash"></i>
                  </button>
                </div>
              </div>

              <div class="d-flex flex-column gap-3">
                <llm-watched-answer :site-answer="item.answers.site" :llm-answer="item.answers.site"/>
                <llm-watched-answer :site-answer="item.answers.site" :llm-answer="item.answers.openai"/>
                <llm-watched-answer :site-answer="item.answers.site" :llm-answer="item.answers.gemini"/>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.issue-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  transition: all 0.2s;
  background: #fff;
}

.issue-header-hover .issue-header {
  background-color: #f8f9fa;
}

.issue-header:not(.collapsed) {
  background-color: #f8f9fa;
}

.issue-header .chevron {
  transition: transform 0.3s ease;
  font-size: 0.75rem;
  opacity: 0.5;
}

.issue-header:not(.collapsed) .chevron {
  transform: rotate(90deg);
  opacity: 1;
}

.issue-text {
  min-width: 0;
}

.details {
  border-bottom: 1px solid #f1f3f5;
  transition: all 0.3s ease;
}

.mini-icon-box {
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  font-size: 0.75rem;
}

.status-icon-box {
  width: 22px;
  height: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  font-size: 0.65rem;
  border: 1px solid currentColor;
}


</style>